import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Sparkles, Facebook } from "lucide-react";
import { TID } from "@/constants/testIds";

const LOGO_URL = "/logo.png";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { label: "Services", to: "/#services", testid: TID.navServices },
    { label: "About", to: "/#about", testid: "nav-about-link" },
    { label: "Reviews", to: "/#reviews", testid: TID.navReviews },
    { label: "Gallery", to: "/#gallery", testid: "nav-gallery-link" },
    { label: "Referrals", to: "/#referrals", testid: "nav-referrals-link" },
    { label: "Contact", to: "/#contact", testid: TID.navContact },
  ];

  return (
    <header className={`transition-all ${scrolled ? "glass" : "bg-transparent backdrop-blur-md bg-black/30"}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-32">
        <Link to="/" data-testid={TID.navLogo} className="flex items-center group">
          <img
            src={LOGO_URL}
            alt="Premier Cleaning Group"
            className="h-52 w-auto object-contain transition-transform group-hover:scale-105"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <a key={l.to} href={l.to} data-testid={l.testid} className="text-sm tracking-wide text-white/80 hover:text-[#D4AF37] transition-colors whitespace-nowrap">
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="https://www.facebook.com/profile.php?id=61590359970893"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-facebook"
            aria-label="Facebook"
            className="text-white/70 hover:text-[#D4AF37] transition-colors p-1"
          >
            <Facebook size={20} strokeWidth={1.5} />
          </a>
          <a href="tel:7573542290" data-testid="nav-phone" className="text-sm flex items-center gap-2 text-white/80 hover:text-[#D4AF37] whitespace-nowrap">
            <Phone size={14} strokeWidth={1.5} /> 757-354-2290
          </a>
          <a href="/#quote" data-testid={TID.navCta} className="btn-gold text-sm whitespace-nowrap">
            <Sparkles size={16} strokeWidth={1.5} /> Get a Quote
          </a>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          data-testid={TID.navMobileToggle}
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass border-t border-white/5 px-6 py-6 flex flex-col gap-5">
          {links.map((l) => (
            <a key={l.to} href={l.to} data-testid={`mobile-${l.testid}`} className="text-white/85 text-base">{l.label}</a>
          ))}
          <a href="tel:7573542290" className="text-white/85 text-base flex items-center gap-2"><Phone size={16}/> 757-354-2290</a>
          <a
            href="https://www.facebook.com/profile.php?id=61590359970893"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-nav-facebook"
            className="text-white/85 text-base flex items-center gap-2"
          >
            <Facebook size={16}/> Facebook
          </a>
          <a href="/#quote" data-testid="mobile-nav-cta" className="btn-gold w-full justify-center"><Sparkles size={16} /> Get a Quote</a>
        </div>
      )}
    </header>
  );
};
